// SteelOps — Public invoice payment portal
//
// Purpose:
//   Lets a customer view an invoice and pay it with a credit card via Stripe
//   Elements — no sign-in required. The Stripe webhook (server-side) marks
//   the invoice paid; this page is purely the user-facing pay flow and
//   receipt.
//
// When reached:
//   App.jsx route /invoice/:token (matched before AuthGate). Token comes from
//   invoices.token, generated when the bid was converted to an invoice.
//
// Who can use it:
//   Anyone with the link. Reads go through the public edge function
//   `invoice-public-get` (anon-key, no JWT). The Stripe PaymentIntent is
//   created server-side by `invoice-payment-intent`. Stripe.js is loaded
//   dynamically from js.stripe.com on first need so the rest of the app
//   doesn't carry the script.
//
// Reads go through `invoice-public-get` (anon-key, no JWT). If the function
// hasn't been deployed yet the call fails fast and the page shows the error.

import { useEffect, useRef, useState } from 'react';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || '';
const ANON_KEY     = import.meta.env.VITE_SUPABASE_ANON_KEY || '';
const STRIPE_JS_URL = 'https://js.stripe.com/v3/';

async function callEdge(name, body) {
  const res = await fetch(`${SUPABASE_URL}/functions/v1/${name}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'apikey':       ANON_KEY,
    },
    body: JSON.stringify(body),
  });
  let data = null;
  try { data = await res.json(); } catch { /* tolerate non-JSON */ }
  return { ok: res.ok, status: res.status, data };
}

function dollars(cents) {
  const n = Number(cents || 0) / 100;
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtDate(d) {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString(); } catch { return String(d); }
}

function statusBadge(status) {
  const map = {
    draft:   { bg: 'var(--s3)',     color: 'var(--t2)',  label: 'Draft' },
    sent:    { bg: 'var(--teal-t)', color: 'var(--teal)', label: 'Awaiting payment' },
    paid:    { bg: 'var(--teal-t)', color: 'var(--teal)', label: 'Paid' },
    overdue: { bg: 'var(--rose-t)', color: 'var(--rose)', label: 'Overdue' },
    void:    { bg: 'var(--s3)',     color: 'var(--t2)',  label: 'Void' },
  };
  const s = map[status] || map.draft;
  return (
    <span style={{
      display: 'inline-block',
      padding: '3px 10px',
      borderRadius: 99,
      fontSize: 11,
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: '.06em',
      background: s.bg,
      color: s.color,
      border: `1px solid ${s.color}33`,
    }}>{s.label}</span>
  );
}

// Inject Stripe.js once. Returns a promise resolving to window.Stripe.
let _stripeJsPromise = null;
function loadStripeJs() {
  if (_stripeJsPromise) return _stripeJsPromise;
  _stripeJsPromise = new Promise((resolve, reject) => {
    if (typeof window === 'undefined') return reject(new Error('SSR'));
    if (window.Stripe) return resolve(window.Stripe);
    const existing = document.querySelector(`script[src="${STRIPE_JS_URL}"]`);
    const onLoad = () => window.Stripe ? resolve(window.Stripe) : reject(new Error('Stripe.js failed to load'));
    if (existing) {
      existing.addEventListener('load', onLoad);
      existing.addEventListener('error', () => reject(new Error('Stripe.js script error')));
      if (window.Stripe) resolve(window.Stripe);
      return;
    }
    const s = document.createElement('script');
    s.src   = STRIPE_JS_URL;
    s.async = true;
    s.onload  = onLoad;
    s.onerror = () => reject(new Error('Stripe.js script error'));
    document.head.appendChild(s);
  });
  return _stripeJsPromise;
}

async function fetchInvoice(token) {
  const edge = await callEdge('invoice-public-get', { token });
  if (edge.ok && edge.data) return { ok: true, data: edge.data };
  return { ok: false, error: edge.data?.error || `Could not load invoice (HTTP ${edge.status})` };
}

export default function InvoicePortal({ token }) {
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);
  const [data,     setData]     = useState(null);
  const [payError, setPayError] = useState(null);
  const [intent,   setIntent]   = useState(null); // { client_secret, publishable_key }
  const [paying,   setPaying]   = useState(false);
  const [paid,     setPaid]     = useState(false);

  const elementRef    = useRef(null);  // host div for Stripe Elements
  const stripeRef     = useRef(null);  // Stripe instance
  const elementsRef   = useRef(null);  // Elements instance
  const paymentElRef  = useRef(null);  // PaymentElement instance

  // Detect ?paid=true from a successful Stripe redirect.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('paid') === 'true') setPaid(true);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await fetchInvoice(token);
      if (cancelled) return;
      if (!res.ok) setError(res.error || 'Could not load invoice');
      else         setData(res.data);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [token]);

  // When we have an intent + the mount node is on screen, init Stripe Elements.
  useEffect(() => {
    if (!intent || !elementRef.current) return;
    let cancelled = false;
    (async () => {
      try {
        const StripeCtor = await loadStripeJs();
        if (cancelled) return;
        const stripe   = StripeCtor(intent.publishable_key);
        const elements = stripe.elements({ clientSecret: intent.client_secret });
        const paymentEl = elements.create('payment');
        paymentEl.mount(elementRef.current);
        stripeRef.current    = stripe;
        elementsRef.current  = elements;
        paymentElRef.current = paymentEl;
      } catch (e) {
        if (!cancelled) setPayError(`Stripe failed to load: ${e.message || e}`);
      }
    })();
    return () => {
      cancelled = true;
      try { paymentElRef.current?.unmount(); } catch { /* ignore */ }
      paymentElRef.current = null;
    };
  }, [intent]);

  async function startPayment() {
    setPayError(null);
    setPaying(true);
    const res = await callEdge('invoice-payment-intent', { token });
    if (!res.ok || !res.data?.client_secret || !res.data?.publishable_key) {
      setPayError(res.data?.error || `Could not start payment (HTTP ${res.status})`);
      setPaying(false);
      return;
    }
    setIntent({
      client_secret:   res.data.client_secret,
      publishable_key: res.data.publishable_key,
    });
    setPaying(false);
  }

  async function confirmPayment() {
    if (!stripeRef.current || !elementsRef.current) return;
    setPayError(null);
    setPaying(true);
    const returnUrl = `${window.location.origin}/invoice/${token}?paid=true`;
    const { error: confirmErr } = await stripeRef.current.confirmPayment({
      elements: elementsRef.current,
      confirmParams: { return_url: returnUrl },
    });
    // If we get here without redirect, confirmPayment errored.
    if (confirmErr) {
      setPayError(confirmErr.message || 'Payment failed');
    }
    setPaying(false);
  }

  if (loading) {
    return (
      <div style={portalShell}>
        <div style={portalCard}>
          <div style={{ color: 'var(--t3)', textAlign: 'center', padding: 40 }}>Loading…</div>
        </div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div style={portalShell}>
        <div style={portalCard}>
          <h2 style={{ marginTop: 0 }}>Invoice unavailable</h2>
          <p style={{ color: 'var(--t2)' }}>{error}</p>
          <p style={{ color: 'var(--t3)', fontSize: 13 }}>
            Check your link. If you believe this is a mistake, reply to the email
            you received.
          </p>
        </div>
      </div>
    );
  }

  const { invoice, customer, company } = data;
  const brand = company?.brand_color || 'var(--teal)';
  const isPaid = paid || invoice.status === 'paid';

  return (
    <div style={portalShell}>
      <div style={portalCard}>
        {/* Brand color stripe */}
        <div style={{ height: 4, background: brand, margin: '-28px -28px 18px', borderRadius: '14px 14px 0 0' }} />

        <header style={{ borderBottom: '1px solid var(--b2)', paddingBottom: 14, marginBottom: 18, display: 'flex', alignItems: 'center', gap: 14 }}>
          {company?.logo_url
            ? <img src={company.logo_url} alt="" style={{ height: 48, width: 48, objectFit: 'contain', borderRadius: 8, background: 'var(--s2)' }} />
            : null}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, color: 'var(--t3)', textTransform: 'uppercase', letterSpacing: '.08em' }}>
              Invoice from
            </div>
            <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--t1)' }}>
              {company?.name || 'Your fabrication shop'}
            </div>
            {customer?.name && (
              <div style={{ marginTop: 4, fontSize: 13, color: 'var(--t2)' }}>
                For <strong>{customer.name}</strong>
              </div>
            )}
          </div>
          <div>{statusBadge(isPaid ? 'paid' : invoice.status)}</div>
        </header>

        {/* Metadata box */}
        <div style={metaBox}>
          <div>
            <div style={metaLabel}>Invoice #</div>
            <div style={metaValue}>{invoice.invoice_number || `#${invoice.id}`}</div>
          </div>
          <div>
            <div style={metaLabel}>Project</div>
            <div style={metaValue}>{invoice.service || invoice.project_name || '—'}</div>
          </div>
          <div>
            <div style={metaLabel}>Issued</div>
            <div style={metaValue}>{fmtDate(invoice.date)}</div>
          </div>
          <div>
            <div style={metaLabel}>Due</div>
            <div style={metaValue}>{fmtDate(invoice.due)}</div>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 22 }}>
          <span style={{ fontSize: 14, color: 'var(--t2)' }}>Amount due</span>
          <span style={{ fontSize: 32, fontWeight: 800 }}>
            {invoice.amount_cents != null ? dollars(invoice.amount_cents) : dollars((invoice.amount || 0) * 100)}
          </span>
        </div>

        {/* Paid state */}
        {isPaid && (
          <div style={successBox}>
            <strong>Paid.</strong> Thanks — your payment has been received. A
            receipt has been emailed to you.
          </div>
        )}

        {/* Unpaid: start payment */}
        {!isPaid && invoice.status !== 'void' && !intent && (
          <div style={{ borderTop: '1px solid var(--b2)', paddingTop: 18 }}>
            {payError && <div style={errorBox}>{payError}</div>}
            <button
              style={btnPay}
              disabled={paying}
              onClick={startPayment}
            >
              {paying ? 'Preparing payment…' : 'Pay this invoice'}
            </button>
            <div style={{ marginTop: 8, fontSize: 11, color: 'var(--t3)', textAlign: 'center' }}>
              Secure payment by Stripe. Your card is never stored on our servers.
            </div>
          </div>
        )}

        {/* Unpaid: stripe form */}
        {!isPaid && intent && (
          <div style={{ borderTop: '1px solid var(--b2)', paddingTop: 18 }}>
            <label style={fieldLabel}>Payment details</label>
            <div
              ref={elementRef}
              style={{
                padding:      12,
                border:       '1px solid var(--b2)',
                borderRadius: 8,
                background:   'var(--s2)',
                minHeight:    48,
              }}
            />
            {payError && <div style={errorBox}>{payError}</div>}
            <button
              style={{ ...btnPay, marginTop: 14 }}
              disabled={paying}
              onClick={confirmPayment}
            >
              {paying
                ? 'Processing…'
                : `Pay ${invoice.amount_cents != null ? dollars(invoice.amount_cents) : dollars((invoice.amount || 0) * 100)}`}
            </button>
          </div>
        )}

        {/* Void */}
        {!isPaid && invoice.status === 'void' && (
          <div style={{ ...successBox, background: 'var(--s2)' }}>
            This invoice has been <strong>voided</strong>. No payment is due.
          </div>
        )}

        <footer style={{ marginTop: 28, fontSize: 11, color: 'var(--t3)', textAlign: 'center' }}>
          Powered by SteelOps
        </footer>
      </div>
    </div>
  );
}

const portalShell = {
  minHeight:       '100vh',
  display:         'flex',
  alignItems:      'flex-start',
  justifyContent:  'center',
  padding:         '32px 16px',
  background:      'var(--bg)',
};
const portalCard = {
  width:           '100%',
  maxWidth:        720,
  background:      'var(--s1)',
  borderRadius:    14,
  border:          '1px solid var(--b1)',
  padding:         '28px 28px 22px',
  boxShadow:       '0 12px 40px rgba(0,0,0,.18)',
  color:           'var(--t1)',
  overflow:        'hidden',
};
const metaBox = {
  display:             'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
  gap:                 12,
  padding:             '14px 16px',
  background:          'var(--s2)',
  borderRadius:        10,
  marginBottom:        18,
};
const metaLabel = {
  fontSize:       10,
  fontWeight:     700,
  color:          'var(--t3)',
  textTransform:  'uppercase',
  letterSpacing:  '.06em',
  marginBottom:   3,
};
const metaValue = {
  fontSize:   14,
  fontWeight: 600,
  color:      'var(--t1)',
};
const fieldLabel = {
  display:        'block',
  fontSize:       12,
  fontWeight:     600,
  color:          'var(--t2)',
  marginBottom:   6,
  textTransform:  'uppercase',
  letterSpacing:  '.05em',
};
const btnPay = {
  width:        '100%',
  padding:      '14px 16px',
  fontSize:     16,
  fontWeight:   700,
  borderRadius: 9,
  border:       'none',
  cursor:       'pointer',
  background:   'var(--teal)',
  color:        '#001216',
};
const successBox = {
  marginTop:    0,
  padding:      14,
  background:   'var(--teal-t)',
  border:       '1px solid var(--teal)',
  borderRadius: 9,
  color:        'var(--t1)',
  fontSize:     14,
};
const errorBox = {
  marginTop:    10,
  marginBottom: 10,
  padding:      10,
  background:   'var(--rose-t)',
  border:       '1px solid var(--rose)',
  borderRadius: 8,
  color:        'var(--t1)',
  fontSize:     13,
};
